<script setup lang="ts">
import { computed, nextTick, ref, watch } from 'vue'
import MmButton from '../base/MmButton.vue'
import MmDialog from '../base/MmDialog.vue'
import MmSwitch from '../base/MmSwitch.vue'
import { useMmPortalTheme } from '../base/portalTheme'
const { t } = useI18n()
const available = useState('analytics-available', () => false)
const choice = useState('analytics-choice', () => 'unknown')
const regionReady = useState('analytics-region-ready', () => false)
const preferencesRequest = useState('privacy-preferences-request', () => 0)
const preferencesOpen = ref(false)
let openedFromBanner = false
const analyticsDraft = ref(false)
const bannerVisible = computed(() => regionReady.value && choice.value === 'unknown' && !preferencesOpen.value)
const origin = ref<HTMLElement | null>(null)
const bannerManage = ref<HTMLButtonElement | null>(null)
const { theme, hue } = useMmPortalTheme(origin, bannerVisible)
function openPreferences(fromBanner = false) {
  if (!available.value) return
  openedFromBanner = fromBanner
  // Editing is a draft. Closing the dialog must never grant or revoke consent.
  analyticsDraft.value = choice.value === 'granted'
  preferencesOpen.value = true
}
watch(preferencesRequest, () => openPreferences())
async function restoreManageFocus() {
  if (!openedFromBanner) return
  await nextTick()
  const target = bannerVisible.value ? bannerManage.value : Array.from(document.querySelectorAll<HTMLElement>('.avatar-btn, .tabbar a[href="/me"]')).find(element => element.getClientRects().length)
  target?.focus({ preventScroll: true })
}
async function choose(value: 'granted' | 'denied') {
  if (!window.musicmintAnalytics) return
  window.musicmintAnalytics.setConsent(value)
  preferencesOpen.value = false
  await restoreManageFocus()
}
function savePreferences() { return choose(analyticsDraft.value ? 'granted' : 'denied') }
</script>

<template>
  <aside v-if="available" ref="origin" class="analytics-consent mm-root" :aria-label="t('analyticsConsent.preferencesTitle')">
    <Teleport to="body">
      <section v-if="bannerVisible" class="analytics-panel mm-root" :data-theme="theme" :style="{ '--live-h': hue }" role="dialog" aria-modal="false" aria-labelledby="analytics-consent-title" aria-describedby="analytics-consent-description">
        <div class="analytics-copy"><h2 id="analytics-consent-title">{{ t('analyticsConsent.title') }}</h2><p id="analytics-consent-description">{{ t('analyticsConsent.description') }}</p><a href="/privacy.html#website-analytics" target="_blank" rel="noopener" class="analytics-policy">{{ t('mm.shell.privacy') }}</a></div>
        <div class="analytics-actions">
          <button ref="bannerManage" type="button" class="btn btn-ghost analytics-settings" aria-haspopup="dialog" @click="openPreferences(true)">{{ t('analyticsConsent.manage') }}</button>
          <MmButton variant="secondary" @click="choose('denied')">{{ t('analyticsConsent.reject') }}</MmButton>
          <MmButton variant="primary" @click="choose('granted')">{{ t('analyticsConsent.allow') }}</MmButton>
        </div>
      </section>
    </Teleport>
    <MmDialog v-model="preferencesOpen" :title="t('analyticsConsent.preferencesTitle')" @close="restoreManageFocus">
      <div class="privacy-center">
        <p class="privacy-intro">{{ t('analyticsConsent.preferencesDescription') }}</p>
        <section class="privacy-category" aria-labelledby="privacy-necessary-title">
          <div class="privacy-category-heading"><h3 id="privacy-necessary-title">{{ t('analyticsConsent.necessaryTitle') }}</h3><span class="privacy-required">{{ t('analyticsConsent.alwaysOn') }}</span></div>
          <p>{{ t('analyticsConsent.necessaryDescription') }}</p>
        </section>
        <section class="privacy-category" aria-labelledby="privacy-analytics-title">
          <div class="privacy-category-heading"><h3 id="privacy-analytics-title">{{ t('analyticsConsent.analyticsTitle') }}</h3><MmSwitch v-model="analyticsDraft" :label="t('analyticsConsent.analyticsToggle')" inline /></div>
          <p>{{ t('analyticsConsent.analyticsDescription') }}</p>
        </section>
        <p class="privacy-note">{{ t('analyticsConsent.preferenceNote') }} <a href="/privacy.html#website-analytics" target="_blank" rel="noopener" class="analytics-policy">{{ t('mm.shell.privacy') }}</a></p>
      </div>
      <template #actions><div class="privacy-actions"><MmButton variant="secondary" @click="choose('granted')">{{ t('analyticsConsent.allow') }}</MmButton><MmButton variant="secondary" @click="choose('denied')">{{ t('analyticsConsent.reject') }}</MmButton><MmButton @click="savePreferences">{{ t('analyticsConsent.save') }}</MmButton></div></template>
    </MmDialog>
  </aside>
</template>

<style scoped>
.analytics-consent { height: 0; }
.analytics-policy { font: 400 var(--t-small)/1.5 var(--font-ui); text-decoration: underline; text-underline-offset: var(--s-1); }
.analytics-panel {
  --consent-bottom: calc(var(--safe-b) + var(--s-4));
  position: fixed; z-index: var(--z-menu); left: 50%; bottom: var(--consent-bottom); transform: translateX(-50%);
  width: min(1080px, calc(100vw - var(--s-8))); max-height: calc(100dvh - var(--consent-bottom) - var(--s-4));
  display: flex; flex-direction: column; overflow: hidden; padding: var(--s-6);
  border: 1px solid var(--line-strong); border-radius: var(--r-xl); background: var(--bg-raised); box-shadow: var(--e-3);
}
.analytics-copy { min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
.analytics-panel h2 { margin: 0; color: var(--text); font: 600 var(--t-body-l)/1.4 var(--font-ui); }
.analytics-panel p { margin: var(--s-3) 0 var(--s-1); color: var(--text-2); font: 400 var(--t-small)/1.7 var(--font-ui); }
.analytics-policy { color: var(--text-2); display: inline-flex; min-height: 44px; align-items: center; }
.analytics-actions { display: flex; justify-content: flex-end; flex-wrap: wrap; flex-shrink: 0; gap: var(--s-3); margin-top: var(--s-3); }
.analytics-actions :deep(.btn), .privacy-actions :deep(.btn) { min-height: 44px; height: auto; white-space: normal; padding: var(--s-3) var(--s-5); font-size: var(--t-small); }
.analytics-policy:focus-visible { outline: 2px solid var(--focus); outline-offset: 3px; }
.privacy-center { display: grid; gap: var(--s-4); text-align: left; }
.privacy-intro, .privacy-category p { color: var(--text-2); font: 400 var(--t-small)/1.7 var(--font-ui); margin: 0; }
.privacy-category { border: 1px solid var(--line); border-radius: var(--r-lg); padding: var(--s-4); background: var(--surface); }
.privacy-category-heading { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: var(--s-2) var(--s-4); margin-bottom: var(--s-2); }
.privacy-category h3 { font: 600 var(--t-body)/1.4 var(--font-ui); margin: 0; }
.privacy-required { display: inline-flex; align-items: center; min-height: 44px; color: var(--text-3); font-size: var(--t-small); }
.privacy-category :deep(.switch) { min-height: 44px; font-size: var(--t-small); }
.privacy-note { color: var(--text-3); font: 400 var(--t-micro)/1.6 var(--font-ui); margin: 0; }
.privacy-note .analytics-policy { font-size: inherit; }
.privacy-actions { display: flex; width: 100%; flex-wrap: wrap; gap: var(--s-3); }
.privacy-actions > :last-child { margin-left: auto; }
:global(.sheet.dialog:has(.privacy-center)) { display: flex; flex-direction: column; overflow: hidden; }
:global(.sheet.dialog:has(.privacy-center) > .dialog-head) { flex-shrink: 0; align-items: center; }
:global(.sheet.dialog:has(.privacy-center) > .dialog-head .icon-btn) { min-width: 44px; min-height: 44px; }
:global(.sheet.dialog:has(.privacy-center) > .privacy-center) { min-height: 0; overflow-y: auto; overscroll-behavior: contain; }
:global(.sheet.dialog:has(.privacy-center) > .dialog-actions) { flex-shrink: 0; padding-top: var(--s-4); border-top: 1px solid var(--line); }
@media (min-width: 768px) { :global(.sheet.dialog:has(.privacy-center)) { width: min(760px, calc(100vw - var(--s-12))); } }
:global(body:has(.dock) .analytics-panel) { --consent-bottom: calc(var(--musicmint-player-height, var(--dock-h)) + var(--s-6) + var(--safe-b)); }
@media (max-width: 1023px) {
  .analytics-panel { width: calc(100vw - var(--s-6)); padding: var(--s-4); }
  :global(body:has(.app-shell .tabbar) .analytics-panel) { --consent-bottom: calc(var(--tab-h) + var(--musicmint-player-height, 0px) + var(--safe-b) + var(--s-3)); }
}
@media (max-width: 899px) {
  :global(body:has(.app-shell .tabbar):has(.noise-mobile-dock) .analytics-panel) { --consent-bottom: calc(var(--tab-h) + var(--musicmint-player-height, 0px) + var(--safe-b) + var(--s-3) + var(--s-16) + var(--s-6)); }
}
@media (max-width: 599px) {
  .privacy-actions { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: var(--s-2); }
  .privacy-actions > :last-child { grid-column: 1 / -1; width: 100%; margin-left: 0; }
  .analytics-actions :deep(.btn), .privacy-actions :deep(.btn) { padding-inline: var(--s-2); }
}
</style>
